import { Plus, Trash2 } from "lucide-react";
import { Btn } from "../../../components/shared/Btn";

/* Step 6 — Location. Full address + map coordinates (manual for now;
   geocoding can be added later) + an optional map embed URL + the
   "Nearby Places" section the customer site renders as a connectivity card. */
export function LocationStep({ data, setData }) {
  const loc = data.location || { address: "", lat: "", lng: "", mapEmbedUrl: "", nearby: [] };
  const setLoc = (patch) => setData({ ...data, location: { ...loc, ...patch } });
  const nearby = loc.nearby || [];

  const addNearby = () =>
    setLoc({ nearby: [...nearby, { id: "n_" + Date.now(), name: "", type: "Office Hub", distance: "" }] });
  const updateNearby = (id, patch) =>
    setLoc({ nearby: nearby.map((n) => (n.id === id ? { ...n, ...patch } : n)) });
  const removeNearby = (id) =>
    setLoc({ nearby: nearby.filter((n) => n.id !== id) });

  const fieldCls = "w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 bg-white focus:outline-none focus:ring-blue-500";
  const labelCls = "text-xs font-medium text-slate-600 mb-1.5 block";

  return (
    <div className="space-y-6">
      <div>
        <label className={labelCls}>Full address</label>
        <textarea
          value={loc.address}
          onChange={(e) => setLoc({ address: e.target.value })}
          placeholder="Survey no., locality, city, state, PIN"
          rows={2}
          className={fieldCls + " resize-y"}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 max-w-md">
        <div>
          <label className={labelCls}>Latitude</label>
          <input
            type="number"
            step="0.000001"
            value={loc.lat}
            onChange={(e) => setLoc({ lat: e.target.value })}
            placeholder="18.6041"
            className={fieldCls + " tabular-nums"}
          />
        </div>
        <div>
          <label className={labelCls}>Longitude</label>
          <input
            type="number"
            step="0.000001"
            value={loc.lng}
            onChange={(e) => setLoc({ lng: e.target.value })}
            placeholder="73.7762"
            className={fieldCls + " tabular-nums"}
          />
        </div>
        <p className="col-span-2 text-[10px] text-slate-400 -mt-2">
          Coordinates drive the map on the property page. You can copy these from Google Maps &mdash; right-click the location and paste the &ldquo;DD&rdquo; format.
        </p>
      </div>

      <div>
        <label className={labelCls}>Map embed URL <span className="text-slate-400 font-normal">(optional)</span></label>
        <input
          value={loc.mapEmbedUrl || ""}
          onChange={(e) => setLoc({ mapEmbedUrl: e.target.value })}
          placeholder="https://www.google.com/maps/embed?pb=..."
          className={fieldCls}
        />
        <p className="text-[10px] text-slate-400 mt-1">
          Optional. Paste any Google Maps link &mdash; the embed &ldquo;src&rdquo; URL, a place/browser URL, or a link with coordinates. We convert it automatically. You can fill this, the lat/long above, or both (the link wins, lat/long is the fallback).
        </p>
      </div>

      <div>
        <div className="flex items-start justify-between mb-2">
          <div>
            <h4 className="text-sm font-semibold text-slate-900">Nearby places</h4>
            <p className="text-xs text-slate-500 mt-0.5">Connectivity highlights shown on the property page.</p>
          </div>
          <Btn variant="secondary" size="sm" onClick={addNearby}>
            <Plus className="w-3.5 h-3.5" />Add place
          </Btn>
        </div>

        {nearby.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400 border border-dashed border-slate-300 rounded-xl">
            No nearby places listed yet.
          </div>
        ) : (
          <div className="space-y-2">
            {nearby.map((n) => (
              <div key={n.id} className="grid grid-cols-2 sm:grid-cols-12 gap-2 items-center bg-white ring-1 ring-slate-200 rounded-lg p-2">
                <div className="sm:col-span-5">
                  <input
                    value={n.name}
                    onChange={(e) => updateNearby(n.id, { name: e.target.value })}
                    placeholder="Name (e.g. Hinjewadi IT Park)"
                    className="w-full text-sm rounded-md ring-1 ring-inset ring-slate-300 px-2.5 py-1.5 focus:outline-none focus:ring-blue-500"
                  />
                </div>
                <div className="sm:col-span-4">
                  <input
                    value={n.type || ""}
                    onChange={(e) => updateNearby(n.id, { type: e.target.value })}
                    placeholder="Type (e.g. Office Hub)"
                    className="w-full text-sm rounded-md ring-1 ring-inset ring-slate-300 px-2.5 py-1.5 focus:outline-none focus:ring-blue-500"
                  />
                </div>
                <div className="sm:col-span-2">
                  <input
                    value={n.distance || ""}
                    onChange={(e) => updateNearby(n.id, { distance: e.target.value })}
                    placeholder="5 Km"
                    className="w-full text-sm rounded-md ring-1 ring-inset ring-slate-300 px-2.5 py-1.5 focus:outline-none focus:ring-blue-500"
                  />
                </div>
                <div className="sm:col-span-1 flex justify-end">
                  <button
                    type="button"
                    onClick={() => removeNearby(n.id)}
                    className="w-7 h-7 rounded-md flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50"
                    title="Remove"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
